/** The Materials tab's dialogs: add a link, edit a material, mark it superseded, remove it, preview Markdown. */

import { useState } from 'react';

import { MaterialIn as MaterialInSchema, materialInTitleMax, materialInUrlOneMax, materialPatchInTitleOneMax } from '@/api/generated/schemas';
import { useAddMaterial, useMaterialText, useRemoveMaterial, useUpdateMaterial } from '@/api/queries/materials';
import type { MaterialAudience, MaterialOut, MaterialType } from '@/api/types/materials';
import { bannerError, oneOf, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { Markdown } from '@/ui/Markdown';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { AudienceField, MaterialTypeField } from './MaterialFields';
import { materialValid } from './materialList';

/** What a dialog needs to write: the asset, the revision to send and where to report the next one. */
export interface MaterialWriter {
  assetId: string;
  revision: number;
  saw: (answer: { revision: number }) => void;
}

function Footer({ onClose, state, label, disabled }: { onClose: () => void; state: ReturnType<typeof buttonState>; label: string; disabled?: boolean }) {
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <ActionButton state={state} label={label} disabled={disabled} />
    </>
  );
}

export function AddLinkDialog({ writer, onClose }: { writer: MaterialWriter; onClose: () => void }) {
  const add = useAddMaterial(writer.assetId);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<MaterialType | ''>('');
  const [audience, setAudience] = useState<MaterialAudience | ''>('');
  const valid = materialValid({ url, title, material_type: type, audience });
  const server = serverFieldErrors(add.error);

  function submit() {
    if (!valid || type === '' || audience === '') return;
    add.mutate(
      { revision: writer.revision, url: url.trim(), title: title.trim(), material_type: type, audience },
      { onSuccess: (answer) => (writer.saw(answer), onClose()) },
    );
  }

  return (
    <Modal title="Add link" size="narrow" onClose={onClose} onSubmit={submit} footer={<Footer onClose={onClose} state={buttonState(add)} label="Add link" disabled={!valid} />}>
      <div className="stack-md">
        <FormError error={bannerError(add.error, ['url', 'title'])} />
        <div className="field">
          <label htmlFor="link-url">Address</label>
          <input id="link-url" type="url" required autoFocus maxLength={materialInUrlOneMax} placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} {...fieldA11y('link-url', server.url, 'link-url-hint')} />
          <span className="hint" id="link-url-hint">An http or https address.</span>
          <FieldError inputId="link-url" message={server.url} />
        </div>
        <div className="field">
          <label htmlFor="link-title">Title</label>
          <input id="link-title" type="text" required maxLength={materialInTitleMax} value={title} {...fieldA11y('link-title', server.title)} onChange={(e) => setTitle(e.target.value)} />
          <FieldError inputId="link-title" message={server.title} />
        </div>
        <MaterialTypeField id="link-type" value={type} onChange={setType} />
        <AudienceField name="link-audience" value={audience} onChange={setAudience} />
      </div>
    </Modal>
  );
}

export function EditMaterialDialog({ writer, material, onClose }: { writer: MaterialWriter; material: MaterialOut; onClose: () => void }) {
  const update = useUpdateMaterial(writer.assetId);
  const [title, setTitle] = useState(material.title);
  const [type, setType] = useState<MaterialType | ''>(oneOf(MaterialInSchema.shape.material_type, material.material_type, ''));
  const [audience, setAudience] = useState<MaterialAudience | ''>(oneOf(MaterialInSchema.shape.audience, material.audience, ''));
  const valid = materialValid({ title, material_type: type, audience });
  const server = serverFieldErrors(update.error);

  function submit() {
    if (!valid || type === '' || audience === '') return;
    update.mutate(
      { id: material.id, change: { revision: writer.revision, title: title.trim(), material_type: type, audience } },
      { onSuccess: (answer) => (writer.saw(answer), onClose()) },
    );
  }

  return (
    <Modal title={`Edit ${material.title}`} size="narrow" onClose={onClose} onSubmit={submit} footer={<Footer onClose={onClose} state={buttonState(update)} label="Save" disabled={!valid} />}>
      <div className="stack-md">
        <FormError error={bannerError(update.error, ['title'])} />
        <div className="field">
          <label htmlFor="material-title">Title</label>
          <input id="material-title" type="text" required autoFocus maxLength={materialPatchInTitleOneMax} value={title} {...fieldA11y('material-title', server.title)} onChange={(e) => setTitle(e.target.value)} />
          <FieldError inputId="material-title" message={server.title} />
        </div>
        <MaterialTypeField id="material-type" value={type} onChange={setType} />
        <AudienceField name="material-audience" value={audience} onChange={setAudience} />
      </div>
    </Modal>
  );
}

/** Marks a material as replaced by a newer one; `replacements` are the current materials it may point to. */
export function SupersedeDialog({
  writer,
  material,
  replacements,
  onClose,
}: {
  writer: MaterialWriter;
  material: MaterialOut;
  replacements: MaterialOut[];
  onClose: () => void;
}) {
  const update = useUpdateMaterial(writer.assetId);
  const [by, setBy] = useState('');

  function submit() {
    if (!by) return;
    update.mutate(
      { id: material.id, change: { revision: writer.revision, superseded_by: by } },
      { onSuccess: (answer) => (writer.saw(answer), onClose()) },
    );
  }

  return (
    <Modal title={`Mark ${material.title} superseded`} size="narrow" onClose={onClose} onSubmit={submit} footer={<Footer onClose={onClose} state={buttonState(update)} label="Mark superseded" disabled={!by} />}>
      <div className="stack-md">
        <FormError error={update.error} />
        <p className="hint">A superseded material moves to older versions: only asset managers see it, and it is never shared with clients.</p>
        {replacements.length === 0 ? (
          <p>Add the newer material first, then mark this one superseded by it.</p>
        ) : (
          <div className="field">
            <label htmlFor="supersede-by">Replaced by</label>
            <select id="supersede-by" required autoFocus value={by} onChange={(e) => setBy(e.target.value)}>
              <option value="" disabled>Choose the newer material</option>
              {replacements.map((m) => (
                <option key={m.id} value={m.id}>{m.title}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </Modal>
  );
}

export function RemoveMaterialDialog({ writer, material, onClose }: { writer: MaterialWriter; material: MaterialOut; onClose: () => void }) {
  const remove = useRemoveMaterial(writer.assetId);
  return (
    <ConfirmDialog
      title={`Remove ${material.title}?`}
      confirmLabel="Remove"
      danger
      pending={remove.isPending}
      error={remove.error}
      onClose={onClose}
      onConfirm={() => remove.mutate({ id: material.id, revision: writer.revision }, { onSuccess: (answer) => (writer.saw(answer), onClose()) })}
    >
      <p>It leaves this asset&apos;s materials. Other assets that use the same file keep it.</p>
    </ConfirmDialog>
  );
}

export function MarkdownPreviewDialog({ assetId, material, onClose }: { assetId: string; material: MaterialOut; onClose: () => void }) {
  const text = useMaterialText(assetId, material.id);
  return (
    <Modal
      title={material.title}
      subtitle={material.file?.filename}
      size="wide"
      onClose={onClose}
      footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>}
    >
      {text.isPending && <p className="hint" role="status">Loading the preview…</p>}
      <FormError error={text.error} />
      {text.data !== undefined && <Markdown headingLevel={4}>{text.data}</Markdown>}
    </Modal>
  );
}
